/* Webinars -> Automation and Messaging.
 *
 * Per campaign, every automation the webinar subsystem can run (the
 * registration link/invite, confirmation, Live nurture, the five
 * reminders, post-webinar CTA message + task, no-show re-invite and the
 * next-day AI voice follow-up): whether it is effectively on and — if not
 * — exactly which switch blocks it, when it fires, which template/agent it
 * uses, who it is for, and exact queued/sent/skipped/failed totals, read
 * from GET /webinar/campaigns/{id}/automation (a read model over the one
 * outbox + durable jobs).
 *
 * Campaign-level controls PATCH the campaign (webinar.config). GLOBAL
 * switches (master, per-reminder-kind defaults) stay in Administration ->
 * Webinar and are only reported here.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, TestBadge } from "@/components/common";
import { Workflow, MessageSquare, PhoneCall, ListChecks, UserPlus, History } from "lucide-react";
import { CampaignPicker, CampaignHeadline, toneClass, useWebinarCaps, fmtInTz } from "@/components/webinar/webinarShared";

const CHANNEL_ICON = { whatsapp: MessageSquare, voice: PhoneCall, task: ListChecks, enrollment: UserPlus };
const PAGE = 25;
const TERMINAL = ["completed", "cancelled"];

const Totals = ({ t, jobs }) => (
  <div className="text-[11px] tabular-nums whitespace-nowrap space-y-0.5">
    <div>
      <span title="Queued">Q {t.queued}</span> · <span title="Sent" className="text-[hsl(var(--success))]">S {t.sent}</span>
      {" · "}<span title="Skipped">Sk {t.skipped}</span>
      {" · "}<span title="Failed" className={t.failed ? "text-[hsl(var(--danger))] font-medium" : ""}>F {t.failed}</span>
      {t.other ? ` · other ${t.other}` : ""}
    </div>
    {jobs && (
      <div className="text-muted-foreground" title="Durable scheduled jobs">
        jobs: {jobs.scheduled} scheduled · {jobs.executed} ran · {jobs.skipped} skipped
        {jobs.failed ? ` · ${jobs.failed} failed` : ""}
      </div>
    )}
  </div>
);

const AutomationRow = ({ item, canEdit, onToggle, onTemplate, availableTemplates, tz }) => {
  const Icon = CHANNEL_ICON[item.channel] || Workflow;
  const tpl = item.template;
  return (
    <TableRow data-testid={`webinar-automation-${item.key}`} className={item.applies ? "" : "opacity-60"}>
      <TableCell className="text-xs align-top min-w-[180px]">
        <div className="flex items-center gap-1.5 font-medium"><Icon className="h-3.5 w-3.5 shrink-0" /> {item.label}</div>
        <p className="text-[10px] text-muted-foreground mt-0.5">{item.trigger}</p>
      </TableCell>
      <TableCell className="text-xs align-top min-w-[160px]">
        {!item.applies ? (
          <span className="text-[11px] text-muted-foreground">N/A — {item.not_applicable_reason}</span>
        ) : (
          <>
            <div className="flex items-center gap-2">
              {item.campaign_switch !== null && item.campaign_switch !== undefined && (
                <Switch checked={!!item.campaign_switch} disabled={!canEdit}
                        data-testid={`webinar-automation-toggle-${item.key}`}
                        onCheckedChange={(v) => onToggle(item.key, v)} />
              )}
              <Badge variant="outline" data-testid={`webinar-automation-state-${item.key}`}
                     className={`text-[10px] ${toneClass(item.effective_enabled ? "success" : "muted")}`}>
                {item.effective_enabled ? "active" : "not running"}
              </Badge>
            </div>
            {item.global_switch === false && (
              <p className="text-[10px] text-[hsl(var(--warning))] mt-0.5">Off globally</p>
            )}
            {item.blocked_by?.length > 0 && (
              <ul className="text-[10px] text-muted-foreground mt-0.5 list-disc pl-3.5">
                {item.blocked_by.map((b) => <li key={b}>{b}</li>)}
              </ul>
            )}
          </>
        )}
      </TableCell>
      <TableCell className="text-xs align-top min-w-[160px]">
        {item.schedule?.rule}
        {item.schedule?.next_run_at && (
          <span className="block text-[10px] text-muted-foreground">next: {fmtInTz(item.schedule.next_run_at, tz, { year: false })}</span>
        )}
      </TableCell>
      <TableCell className="text-xs align-top min-w-[170px]">
        {tpl ? (
          <>
            {canEdit && item.applies ? (
              <Select value={tpl.template_kind} onValueChange={(v) => onTemplate(item.key, v)}>
                <SelectTrigger className="h-7 text-[11px]" data-testid={`webinar-template-${item.key}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={tpl.default_template_kind}>{tpl.default_template_kind} (default)</SelectItem>
                  {availableTemplates.filter((a) => a.template_kind !== tpl.default_template_kind).map((a) => (
                    <SelectItem key={a.template_kind} value={a.template_kind}>{a.template_kind}</SelectItem>
                  ))}
                  {tpl.template_kind !== tpl.default_template_kind
                    && !availableTemplates.some((a) => a.template_kind === tpl.template_kind) && (
                    <SelectItem value={tpl.template_kind}>{tpl.template_kind}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            ) : <span className="font-mono text-[11px]">{tpl.template_kind}</span>}
            <span className={`block text-[10px] mt-0.5 ${tpl.configured ? "text-muted-foreground" : "text-[hsl(var(--danger))]"}`}>
              {tpl.configured ? `Interakt: ${tpl.template_name} (${tpl.language || "en"})` : "No approved Interakt template mapped — sends will fail"}
            </span>
          </>
        ) : item.agent ? (
          <span>
            {item.agent.name}
            <span className={`block text-[10px] ${item.agent.configured ? "text-muted-foreground" : "text-[hsl(var(--danger))]"}`}>
              {item.agent.configured ? "Agent configured" : "Agent not configured"}
            </span>
          </span>
        ) : <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="text-xs align-top">
        {item.audience ? (
          <><span className="tabular-nums font-medium">{item.audience.count}</span>
            <span className="block text-[10px] text-muted-foreground">{item.audience.label}</span></>
        ) : "—"}
      </TableCell>
      <TableCell className="align-top">
        {item.channel === "task" ? (
          <span className="text-[11px] tabular-nums">
            {item.task_totals?.created ?? 0} created
            {item.task_totals?.not_created ? <span className="block text-[10px] text-muted-foreground">{item.task_totals.not_created} eligible with no task (task off, or no opportunity)</span> : null}
          </span>
        ) : item.channel === "enrollment" ? (
          <span className="text-[11px] tabular-nums">{item.reinvite_totals?.reinvited ?? 0} re-invited</span>
        ) : (
          <>
            <Totals t={item.totals} jobs={item.jobs} />
            {item.call_outcomes && Object.keys(item.call_outcomes).length > 0 && (
              <p className="text-[10px] text-muted-foreground">
                calls: {Object.entries(item.call_outcomes).map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(" · ")}
              </p>
            )}
          </>
        )}
        {/* Failure / skip reason + retry hint live under the totals (one
            column fewer, so the table fits without horizontal scroll). */}
        <div className="text-[11px] max-w-[220px] mt-0.5">
        {item.last_failure?.reason && (
          <p className="text-[hsl(var(--danger))] truncate" title={item.last_failure.reason}>{item.last_failure.reason}</p>
        )}
        {item.skip_reasons?.length > 0 && (
          <p className="text-[10px] text-muted-foreground" title={item.skip_reasons.map((s) => `${s.reason} (${s.count})`).join("\n")}>
            skipped: {item.skip_reasons[0].reason} ({item.skip_reasons[0].count})
          </p>
        )}
        {item.retry && (
          <p className="text-[10px] text-muted-foreground">{item.retry.supported ? "Retry: Failures tab" : "No manual retry"}</p>
        )}
        </div>
      </TableCell>
    </TableRow>
  );
};

const FollowUpSettings = ({ campaign, campaigns, canEdit, onPatch, automation }) => {
  const post = campaign.post_webinar || {};
  const nurture = campaign.nurture || {};
  const [days, setDays] = useState("");
  useEffect(() => { setDays((nurture.messages || []).map((m) => m.days_before).join(", ")); },
            [campaign.campaign_id, nurture.messages]);
  const voice = automation.items.find((i) => i.key === "anika_followup");
  const callWindow = voice?.schedule?.call_window || {};
  const setPost = (patch) => onPatch({ post_webinar: { ...post, ...patch } });
  const saveDays = () => {
    const list = days.split(",").map((s) => Number(s.trim())).filter((n) => Number.isFinite(n) && n > 0);
    if (list.some((n) => n < 2)) { toast.error("Every nurture day must be 2 or more days before (strictly before D-1)"); return; }
    if (list.length > 4) { toast.error("At most 4 nurture messages"); return; }
    const hours = Object.fromEntries((nurture.messages || []).map((m) => [m.days_before, m.send_hour_ist]));
    onPatch({ nurture: { enabled: list.length > 0 && nurture.enabled !== false,
                         messages: list.map((d) => ({ days_before: d, ...(hours[d] != null ? { send_hour_ist: hours[d] } : {}) })) } });
  };
  const targets = (campaigns || []).filter((c) => c.campaign_id !== campaign.campaign_id
    && c.campaign_type === "live" && !TERMINAL.includes(c.status));
  return (
    <Card data-testid="webinar-followup-settings">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading">Campaign follow-up settings</CardTitle>
        <p className="text-[11px] text-muted-foreground">On/off for each automation is in the table above.</p>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 text-xs">
        <div>
          <Label className="text-xs">CTA threshold</Label>
          <Select value={String(post.cta_threshold ?? 50)} disabled={!canEdit}
                  onValueChange={(v) => setPost({ cta_threshold: Number(v) })}>
            <SelectTrigger className="h-8" data-testid="webinar-cta-threshold"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="50">Attended 50% or more</SelectItem>
              <SelectItem value="75">Attended 75% or more</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground mt-1">Decides who gets the CTA message, task and voice follow-up.</p>
        </div>
        <div>
          <Label className="text-xs">Voice follow-up time (IST, next day)</Label>
          <Select value={String(post.anika_followup_hour_ist ?? 11)} disabled={!canEdit}
                  onValueChange={(v) => setPost({ anika_followup_hour_ist: Number(v) })}>
            <SelectTrigger className="h-8" data-testid="webinar-voice-hour"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: 12 }, (_, i) => i + 9).map((h) => (
                <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground mt-1" data-testid="webinar-call-window">
            Always clamped into the global call window {callWindow.start || "10:00"}–{callWindow.end || "21:30"} IST
            (Administration → Ops policies).
          </p>
        </div>
        <div>
          <Label className="text-xs">No-show re-invite target</Label>
          <Select value={post.noshow_reinvite_campaign_id || "__same__"} disabled={!canEdit}
                  onValueChange={(v) => setPost({ noshow_reinvite_campaign_id: v === "__same__" ? null : v })}>
            <SelectTrigger className="h-8" data-testid="webinar-noshow-target"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__same__">This campaign&apos;s next open session</SelectItem>
              {targets.map((c) => <SelectItem key={c.campaign_id} value={c.campaign_id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground mt-1">Only re-invites while the target is active and has an open session.</p>
        </div>
        {campaign.campaign_type === "live" ? (
          <div>
            <Label className="text-xs">Nurture days before (max 4, each ≥ 2)</Label>
            <div className="flex gap-1.5">
              <Input className="h-8" value={days} disabled={!canEdit} placeholder="5, 4, 3, 2"
                     data-testid="webinar-nurture-days" onChange={(e) => setDays(e.target.value)} />
              {canEdit && <Button size="sm" variant="outline" className="h-8" onClick={saveDays}
                                  data-testid="webinar-nurture-days-save">Save</Button>}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Sent 11:00 in the campaign timezone ({campaign.timezone || "Asia/Kolkata"}) on each day unless configured otherwise.</p>
          </div>
        ) : (
          <div className="text-[11px] text-muted-foreground">Evergreen campaigns have no nurture sequence.</div>
        )}
      </CardContent>
    </Card>
  );
};

const VoiceQueue = ({ campaignId, refreshKey }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/webinar/campaigns/${campaignId}/voice-followups?limit=25`)
      .then((r) => { if (alive) setData(r.data); }).catch((e) => toast.error(errDetail(e)));
    return () => { alive = false; };
  }, [campaignId, refreshKey]);
  return (
    <Card data-testid="webinar-voice-queue">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <PhoneCall className="h-4 w-4" /> Voice follow-up call queue ({data?.total ?? 0})
        </CardTitle>
        <p className="text-[11px] text-muted-foreground">
          Failed calls are retried by the AI Voice pipeline&apos;s own bounded retry — never re-dispatched from here.
        </p>
      </CardHeader>
      <CardContent>
        {!data ? null : data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-3 text-center">No voice follow-ups yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Customer</TableHead><TableHead>Queued</TableHead>
                <TableHead>Result</TableHead><TableHead>Dispatch</TableHead><TableHead>Error</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.items.map((v) => (
                  <TableRow key={v.voice_attempt_id}>
                    <TableCell className="text-xs">
                      <Link to={`/customers/${v.contact_id}`} className="hover:underline">{v.contact_name || v.contact_id}</Link>
                      {v.is_test_record && <TestBadge show />}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{fmtIST(v.created_at)}</TableCell>
                    <TableCell className="text-xs">{(v.outcome || "—").replace(/_/g, " ")}</TableCell>
                    <TableCell className="text-xs">{(v.dispatch_state || "—").replace(/_/g, " ")}</TableCell>
                    <TableCell className="text-[11px] text-[hsl(var(--danger))] max-w-[260px] truncate" title={v.last_error || ""}>{v.last_error || ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DeliveryHistory = ({ campaignId, automation, refreshKey }) => {
  const [filterKey, setFilterKey] = useState("");
  const [bucket, setBucket] = useState("");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  useEffect(() => { setOffset(0); }, [campaignId, filterKey, bucket]);
  useEffect(() => {
    let alive = true;
    const qs = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
    if (filterKey) qs.set("automation", filterKey);
    if (bucket) qs.set("status_bucket", bucket);
    api.get(`/webinar/campaigns/${campaignId}/deliveries?${qs}`)
      .then((r) => { if (alive) setData(r.data); }).catch((e) => toast.error(errDetail(e)));
    return () => { alive = false; };
  }, [campaignId, filterKey, bucket, offset, refreshKey]);
  const options = automation.items.filter((i) => ["whatsapp", "voice"].includes(i.channel));
  const total = data?.total ?? 0;
  return (
    <Card data-testid="webinar-delivery-history">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0 gap-2 flex-wrap">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <History className="h-4 w-4" /> Delivery history ({total})
        </CardTitle>
        <div className="flex gap-2 flex-wrap">
          <Select value={filterKey || "__all__"} onValueChange={(v) => setFilterKey(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-8 w-[220px]" data-testid="webinar-delivery-automation"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All automations</SelectItem>
              {options.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={bucket || "__all__"} onValueChange={(v) => setBucket(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-8 w-[140px]" data-testid="webinar-delivery-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              {["queued", "sent", "skipped", "failed"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {!data ? null : data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-3 text-center" data-testid="webinar-delivery-empty">Nothing delivered or queued yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>When</TableHead><TableHead>Automation</TableHead><TableHead>Customer</TableHead>
                <TableHead>Template</TableHead><TableHead>Status</TableHead><TableHead>Error</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.items.map((d) => (
                  <TableRow key={d.outbox_id}>
                    <TableCell className="text-xs whitespace-nowrap">{fmtIST(d.created_at)}</TableCell>
                    <TableCell className="text-xs">{d.automation_label}</TableCell>
                    <TableCell className="text-xs">
                      {d.contact_id ? <Link to={`/customers/${d.contact_id}`} className="hover:underline">{d.contact_name || d.contact_id}</Link> : "—"}
                      {d.is_test_record && <TestBadge show />}
                    </TableCell>
                    <TableCell className="text-[11px] font-mono">{d.template_kind || "—"}</TableCell>
                    <TableCell className="text-xs">
                      <Badge variant="outline" className={`text-[10px] ${toneClass(
                        d.status_bucket === "sent" ? "success" : d.status_bucket === "failed" ? "danger"
                          : d.status_bucket === "queued" ? "warning" : "muted")}`}>
                        {d.status.replace(/_/g, " ")}
                      </Badge>
                      {d.attempts > 1 && <span className="ml-1 text-[10px] text-muted-foreground">{d.attempts} attempts</span>}
                    </TableCell>
                    <TableCell className="text-[11px] text-[hsl(var(--danger))] max-w-[260px] truncate" title={d.last_error || ""}>{d.last_error || ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {total > PAGE && (
          <div className="flex items-center justify-end gap-2 pt-2 text-xs">
            <span className="text-muted-foreground">{offset + 1}–{Math.min(offset + PAGE, total)} of {total}</span>
            <Button size="sm" variant="outline" className="h-7" disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</Button>
            <Button size="sm" variant="outline" className="h-7" disabled={offset + PAGE >= total}
                    onClick={() => setOffset(offset + PAGE)}>Next</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const WebinarAutomation = ({ campaigns, campaignId, onCampaignChange, onChanged, refreshKey }) => {
  const { canConfig } = useWebinarCaps();
  const [automation, setAutomation] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [localKey, setLocalKey] = useState(0);

  const load = useCallback(async () => {
    if (!campaignId) { setAutomation(null); setCampaign(null); return; }
    try {
      const [a, c] = await Promise.all([
        api.get(`/webinar/campaigns/${campaignId}/automation`),
        api.get(`/webinar/campaigns/${campaignId}`),
      ]);
      setAutomation(a.data);
      setCampaign(c.data.campaign);
    } catch (e) { toast.error(errDetail(e)); }
  }, [campaignId]);
  useEffect(() => { load(); }, [load, refreshKey, localKey]);

  const canEdit = canConfig && campaign && !TERMINAL.includes(campaign.status);

  const patch = async (body) => {
    try {
      await api.patch(`/webinar/campaigns/${campaignId}`, body);
      toast.success("Campaign automation updated");
      // The page-level refresh (onChanged) reloads this tab too; only bump
      // the local key when rendered standalone.
      if (onChanged) onChanged(); else setLocalKey((k) => k + 1);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const onToggle = (key, value) => {
    if (!campaign) return;
    const post = campaign.post_webinar || {};
    if (["reminder_d1_2100", "reminder_dayof_0900", "reminder_t60", "reminder_t15", "reminder_ontime"].includes(key)) {
      patch({ reminders: { ...(campaign.reminders || {}), [key]: value } });
    } else if (key === "nurture") {
      const n = campaign.nurture || {};
      if (value && !(n.messages || []).length) { toast.error("Set nurture days first (Campaign follow-up settings below)"); return; }
      patch({ nurture: { ...n, enabled: value } });
    } else if (key === "post_webinar_cta") {
      patch({ post_webinar: { ...post, cta_message_enabled: value } });
    } else if (key === "cta_task") {
      patch({ post_webinar: { ...post, cta_task_enabled: value } });
    } else if (key === "noshow_reinvite") {
      patch({ post_webinar: { ...post, noshow_reinvite_enabled: value } });
    } else if (key === "anika_followup") {
      patch({ post_webinar: { ...post, anika_followup_enabled: value } });
    }
  };

  const onTemplate = (key, templateKind) => {
    if (!campaign) return;
    patch({ template_kinds: { ...(campaign.template_kinds || {}), [key]: templateKind } });
  };

  const applicable = automation?.items.filter((i) => i.applies) || [];
  const notApplicable = automation?.items.filter((i) => !i.applies) || [];

  return (
    <div className="space-y-4" data-testid="webinar-automation">
      <div className="flex items-center gap-2 flex-wrap">
        <CampaignPicker campaigns={campaigns} value={campaignId} onChange={onCampaignChange} />
        {campaign && <CampaignHeadline campaign={campaign} />}
      </div>
      {!campaignId ? (
        <EmptyState icon={Workflow} title="Choose a campaign" hint="Automations are configured and reported per campaign."
                    testId="webinar-automation-empty" />
      ) : !automation || !campaign ? null : (
        <>
          <div className="rounded-md border p-2.5 text-xs flex flex-wrap gap-x-4 gap-y-1" data-testid="webinar-automation-status">
            <span>Master switch: <strong className={automation.master_enabled ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]"}>
              {automation.master_enabled ? "ON" : "OFF"}</strong></span>
            <span>Campaign: <strong>{automation.campaign_status}</strong></span>
            <span>Mode: <strong>{automation.test_mode ? "TEST" : "LIVE"}</strong></span>
            <span>WhatsApp: <strong>{automation.channels.whatsapp_enabled ? "on" : "off"}</strong></span>
            <span>Voice: <strong>{automation.channels.voice_enabled ? "on" : "off"}</strong></span>
            <span>TEST sends: <strong>{automation.channels.allow_test_sends ? "allowed" : "blocked"}</strong></span>
            <span>Timezone: <strong data-testid="webinar-automation-timezone">{automation.timezone}</strong></span>
            <span>Next session: <strong>{automation.next_session ? fmtInTz(automation.next_session.scheduled_start, automation.timezone) : "none scheduled"}</strong></span>
            {TERMINAL.includes(campaign.status) && (
              <span className="text-muted-foreground">This campaign is {campaign.status} — its settings are read-only.</span>
            )}
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading flex items-center gap-2"><Workflow className="h-4 w-4" /> Automations</CardTitle>
              <p className="text-[11px] text-muted-foreground">
                Q queued · S sent · Sk skipped · F failed — exact counts from the delivery outbox.
                Global per-reminder switches and the master switch live in Administration → Webinar.
              </p>
              {/* One note per channel (same for every row of that channel),
                  instead of repeating it on each automation. */}
              {[...new Set(applicable.map((i) => i.delivery_note).filter(Boolean))].map((note) => (
                <p key={note} className="text-[11px] text-[hsl(var(--warning))]" data-testid="webinar-delivery-note">{note}</p>
              ))}
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table data-testid="webinar-automation-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Automation</TableHead><TableHead>Enabled</TableHead>
                      <TableHead>Schedule</TableHead><TableHead>Template / agent</TableHead>
                      <TableHead>Audience</TableHead><TableHead>Totals / failures</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...applicable, ...notApplicable].map((item) => (
                      <AutomationRow key={item.key} item={item} canEdit={canEdit} tz={automation.timezone}
                                     availableTemplates={automation.available_templates || []}
                                     onToggle={onToggle} onTemplate={onTemplate} />
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <FollowUpSettings campaign={campaign} campaigns={campaigns} canEdit={canEdit}
                            automation={automation} onPatch={patch} />
          <VoiceQueue campaignId={campaignId} refreshKey={`${refreshKey}-${localKey}`} />
          <DeliveryHistory campaignId={campaignId} automation={automation} refreshKey={`${refreshKey}-${localKey}`} />
        </>
      )}
    </div>
  );
};

export default WebinarAutomation;
